import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import PeriodOrgFilter from '../components/PeriodOrgFilter'
import { api } from '../lib/api'
import ListContainer from '../ui/ListContainer'
import { downloadCsv } from '../utils/csv'
import MenuGuideButton from '../components/MenuGuideButton'

export default function ReportsPage() {
    const [data, setData] = useState([])
    // const [trendData, setTrendData] = useState([])
    const [statusData, setStatusData] = useState([])
    const [orgId, setOrgId] = useState('')
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(1)
    const [size, setSize] = useState(10)
    const [sort, setSort] = useState('value,DESC')
    const [error, setError] = useState(null)

    // 기본 기간: 최근 90일 (데이터가 "아예 안나오는" 상황을 줄이기 위해 기본 범위를 넓힘)
    useEffect(() => {
        if (from || to) return
        const pad2 = (n) => String(n).padStart(2, '0')
        const fmt = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`
        const end = new Date()
        const start = new Date(end)
        start.setDate(end.getDate() - 89)
        setFrom(fmt(start))
        setTo(fmt(end))
    }, [from, to])

    // 데이터 로딩 함수
    const loadReportsData = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const params = {}
            if (orgId) params.orgId = orgId
            if (from) params.from = from
            if (to) params.to = to
            params.page = page - 1
            params.size = size
            params.sort = sort

            const [branchRes, statusRes] = await Promise.all([
                api.get('/reports/branch-summary', { params }),
                // api.get('/reports/trend', { params }),
                api.get('/reports/status-distribution', { params })
            ])

            const branch = Array.isArray(branchRes.data?.data) ? branchRes.data.data : (branchRes.data?.items || branchRes.data || [])
            // const trend = Array.isArray(trendRes.data?.data) ? trendRes.data.data : (trendRes.data?.items || trendRes.data || [])
            const status = statusRes.data?.data || statusRes.data || { distribution: [] }

            // 서버 응답 키를 UI에서 기대하는 키로 매핑
            const mappedBranch = branch.map(it => ({
                name: it.branchName || it.name || '—',
                value: it.totalRequests ?? it.value ?? 0,
                completed: it.completedRequests ?? it.completed ?? 0,
                inProgress: it.inProgressRequests ?? it.inProgress ?? 0,
                pending: it.pendingRequests ?? it.pending ?? 0,
                avgDays: it.avgProcessingDays ?? it.avgDays ?? 0,
            }))
            // const mappedTrend = trend.map(it => ({
            //     date: it.month || it.date || '—',
            //     newRequests: it.requests ?? it.newRequests ?? 0,
            //     completedRequests: it.completed ?? it.completedRequests ?? 0,
            //     pendingRequests: it.pending ?? it.pendingRequests ?? 0,
            // }))
            const mappedStatus = (status.distribution || status).map?.(it => ({
                name: it.status || it.name,
                value: it.count ?? it.value ?? 0,
                color: it.color,
            })) || []

            setData(mappedBranch)
            // setTrendData(mappedTrend)
            setStatusData(mappedStatus)
        } catch (error) {
            console.error('Failed to load reports data:', error)
            setError('리포트 데이터를 불러오지 못했습니다')
            setData([])
            // setTrendData([])
            setStatusData([])
        } finally {
            setLoading(false)
        }
    }, [orgId, from, to, page, size, sort])

    useEffect(() => {
        loadReportsData()
    }, [loadReportsData])

    const summary = useMemo(() => {
        const total = data.reduce((acc, cur) => acc + (Number(cur.value) || 0), 0)
        const completed = data.reduce((acc, cur) => acc + (Number(cur.completed) || 0), 0)
        const inProgress = data.reduce((acc, cur) => acc + (Number(cur.inProgress) || 0), 0)
        const pending = data.reduce((acc, cur) => acc + (Number(cur.pending) || 0), 0)
        const avgDays = data.reduce((acc, cur) => acc + (Number(cur.avgDays) || 0), 0) / Math.max(data.length, 1)

        return {
            totalRequests: total,
            avgDays: avgDays ? Math.round(avgDays) : '—',
            fixRatio: total ? Math.round((completed / total) * 100) : '—',
            completionRate: total ? Math.round((completed / total) * 100) : '—',
            inProgressRate: total ? Math.round((inProgress / total) * 100) : '—',
            pendingRate: total ? Math.round((pending / total) * 100) : '—'
        }
    }, [data])

    // 파이 차트 데이터 준비
    const pieData = (statusData || []).map(item => ({ name: item.name, value: Number(item.value) || 0, color: item.color || '#1E3A8A' })).filter(item => item.value > 0)

    const detailHeader = null
    const detailTable = (
        <div>
            {/* 상세 분석 테이블 */}
            <div className="p-4">
                <h3 className="text-lg font-semibold mb-4">조직별 상세 분석</h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                        <thead className="bg-slate-50">
                            <tr>
                                <th className="px-4 py-2 text-left cursor-pointer select-none hover:bg-slate-100" onClick={() => setSort('name,ASC')}>조직</th>
                                <th className="px-4 py-2 text-left cursor-pointer select-none hover:bg-slate-100" onClick={() => setSort('value,DESC')}>총 의뢰</th>
                                <th className="px-4 py-2 text-left">완료</th>
                                <th className="px-4 py-2 text-left">진행중</th>
                                <th className="px-4 py-2 text-left">대기</th>
                                <th className="px-4 py-2 text-left">완료율</th>
                                <th className="px-4 py-2 text-left">평균 처리일</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((item, index) => (
                                <tr key={index} className="border-t">
                                    <td className="px-4 py-2 font-medium">{item.name}</td>
                                    <td className="px-4 py-2">{item.value}</td>
                                    <td className="px-4 py-2 text-green-600">{item.completed || 0}</td>
                                    <td className="px-4 py-2 text-blue-600">{item.inProgress || 0}</td>
                                    <td className="px-4 py-2 text-yellow-600">{item.pending || 0}</td>
                                    <td className="px-4 py-2">{item.value ? Math.round(((item.completed || 0) / item.value) * 100) : 0}%</td>
                                    <td className="px-4 py-2">{item.avgDays || '—'}일</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )

    const detailFooter = (
        <div className="flex items-center gap-2 p-3 border-t text-sm">
            <select className="rounded border px-3 py-2 text-sm" value={size} onChange={(e) => { setSize(Number(e.target.value)); setPage(1) }}>
                <option value={10}>10개</option>
                <option value={20}>20개</option>
                <option value={50}>50개</option>
            </select>
            <button className="rounded border px-3 py-1" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>이전</button>
            <span className="text-sm text-slate-600">페이지 {page}</span>
            <button className="rounded border px-3 py-1" onClick={() => setPage(p => p + 1)}>다음</button>
        </div>
    )

    return (
        <section>
            <div className="flex items-center justify-between">
                <div>
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-2xl font-bold">통계</h1>
                        <MenuGuideButton slug="reports" title="통계 가이드" />
                    </div>
                </div>
                {loading && (
                    <div className="text-sm text-slate-500">데이터 로딩 중…</div>
                )}
            </div>

            <div className="mt-4">
                <PeriodOrgFilter orgId={orgId} from={from} to={to} onChange={({ orgId, from, to }) => { setOrgId(orgId); setFrom(from); setTo(to) }} />
            </div>

            {/* 요약 카드 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="rounded border p-4"><div className="text-sm text-slate-600">총 설계 의뢰 수</div><div className="text-3xl font-semibold mt-1">{summary.totalRequests}</div></div>
                <div className="rounded border p-4"><div className="text-sm text-slate-600">평균 처리기간</div><div className="text-3xl font-semibold mt-1">{summary.avgDays}일</div></div>
                <div className="rounded border p-4"><div className="text-sm text-slate-600">완료율</div><div className="text-3xl font-semibold mt-1 text-green-600">{summary.completionRate}%</div></div>
            </div>

            {/* 추가 지표 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                <div className="rounded border p-4"><div className="text-sm text-slate-600">진행률</div><div className="text-2xl font-semibold mt-1 text-blue-600">{summary.inProgressRate}%</div></div>
                <div className="rounded border p-4"><div className="text-sm text-slate-600">대기율</div><div className="text-2xl font-semibold mt-1 text-yellow-600">{summary.pendingRate}%</div></div>
                <div className="rounded border p-4"><div className="text-sm text-slate-600">효율성 지수</div><div className="text-2xl font-semibold mt-1 text-brand-navy">{summary.completionRate !== '—' && summary.avgDays !== '—' ? Math.round(Number(summary.completionRate) / Number(summary.avgDays)) : '—'}</div></div>
            </div>

            {/* 차트들 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                <div className="rounded border p-4 h-72"><h3 className="text-lg font-semibold mb-4">조직별 의뢰 현황</h3><ResponsiveContainer width="100%" height="100%"><BarChart data={data}><CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="name" /><YAxis /><Tooltip /><Bar dataKey="value" fill="#1E3A8A" /></BarChart></ResponsiveContainer></div>
                <div className="rounded border p-4 h-72"><h3 className="text-lg font-semibold mb-4">상태별 분포</h3><ResponsiveContainer width="100%" height="100%"><PieChart><Pie data={pieData} cx="50%" cy="50%" labelLine={false} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} outerRadius={80} fill="#8884d8" dataKey="value">{pieData.map((entry, index) => (<Cell key={`cell-${index}`} fill={entry.color} />))}</Pie><Tooltip /></PieChart></ResponsiveContainer></div>
            </div>

            {/* 상세 분석: ListContainer 적용 */}
            <div className="mt-6 rounded border">
                <ListContainer
                    loading={loading}
                    error={error}
                    onRetry={() => loadReportsData()}
                    header={detailHeader}
                    table={detailTable}
                    footer={detailFooter}
                />
            </div>

            {/* 내보내기 */}
            <div className="mt-6 flex gap-2">
                <button className="hidden md:inline-flex rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={async () => {
                    try {
                        const params = {}
                        if (orgId) params.orgId = orgId
                        if (from) params.from = from
                        if (to) params.to = to
                        const res = await api.get('/reports/branch-summary/export', { params, responseType: 'blob' })
                        const url = URL.createObjectURL(res.data)
                        const a = document.createElement('a')
                        a.href = url
                        a.download = 'reports.csv'
                        a.click()
                        URL.revokeObjectURL(url)
                    } catch {
                        downloadCsv('reports.csv', data)
                    }
                }}>CSV 내보내기</button>
                <button className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => { console.log('PDF 리포트 생성 기능은 향후 구현 예정') }}>리포트 다운로드</button>
            </div>
        </section>
    )
}

